import React, { useState, useEffect } from "react";
import Image from "../../assets/avatar.png";
import GenericModal from "components/Modal";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/20/solid";
import { colors } from "themes";
import UpdateSalaryDetails from "pages/payrole/salary/UpdateSalaryDetails";
import WorkSummary from "./WorkSummary";
import AssetsList, { Asset } from "../../components/AssetsList";
import apiService from "../../services/apiService";
import { formatDateToMonthDayYear } from "../../util/Util";
// MediaPreview: full-screen lightbox with zoom/nav/swipe for documents
import MediaPreview, {
  useMediaPreview,
  MediaItem,
  triggerDownload,
  resolveDownloadName,
} from "components/MediaPreview";
import {
  isGoogleDriveUrl,
  getGoogleDriveThumbnailUrl,
} from "util/googleDriveHelper";
import { useToaster } from "contexts/toasterContext";
import { parseJwtPayload, isAdminRole } from "util/moduleAccess";
import EditEmployee from "./EditEmployee";
import HeaderButtons from "components/Button/HeaderButtons";

interface Skill {
  id: string | number;
  skill: string;
}

interface TabSection {
  key: string;
  label: string;
  icon?: JSX.Element;
}

interface EmployeeDetailProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allEmployees: any[];
  currentIndex: number;
  onNavigate: (newIndex: number) => void;
  onBack: () => void;
  // Called after an in-screen edit succeeds so the parent list can refetch and
  // the table row reflects the change when the user navigates back.
  onEmployeeUpdated?: () => void;
  sections?: TabSection[];
  /**
   * CHANGE: which tab to open on. Added for the DOCUMENT_REVIEW_PENDING
   * notification, which must land an admin directly on this employee's
   * Documents tab to approve or reject — arriving on Personal and asking them
   * to find it defeats the point of the deep link.
   *
   * NOTE: a section KEY (e.g. "Documents"), not a label. Ignored when it names
   * no section, so a stale URL opens the first tab instead of a blank screen.
   */
  initialSection?: string;
}

interface Project {
  projectId: number;
  name: string;
  manager: string;
  teamSize: string;
  startDate: string;
  completionDate: string;
  clientContactName: string;
  clientContactNumber: string;
  projectDetail: string;
  emails: string;
  comments?: string;
  projectAssignedDate: string;
}

interface Document {
  id: number;
  documentName: string;
  userId: number;
  s3Url: string;
  isVerified?: boolean;
  // Authoritative verification state from the API.
  verificationStatus?: string | null;
  rejectionReason?: string | null;
  fileType?: string | null;
  size?: number | null;
  uploadedAt?: string | null;
}

// Document verification lifecycle. The API returns these on each document.
type DocStatus = "PENDING" | "APPROVED" | "REJECTED";

// Normalise the API's verificationStatus into one of three states, falling back
// to the legacy `isVerified` boolean when the status string is absent.
function getDocStatus(doc: Document): DocStatus {
  const s = (doc.verificationStatus || "").toUpperCase();
  if (s === "APPROVED" || s === "VERIFIED") return "APPROVED";
  if (s === "REJECTED") return "REJECTED";
  if (!doc.verificationStatus && doc.isVerified) return "APPROVED";
  return "PENDING";
}

interface ChildDetail {
  id: string | number;
  childName: string;
  childDob: string;
  gender: string;
}

interface FamilyDetailsData {
  fatherName?: string;
  motherName?: string;
  maritalStatus?: string;
  spouseName?: string;
  bloodGroup?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  numberOfChildren?: number;
  children?: ChildDetail[];
  [key: string]: string | number | ChildDetail[] | undefined;
}

function capitalizeWords(text: string) {
  return text
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// Derive a short human-readable label from MIME type or URL extension (e.g. "PNG", "PDF")
function getFileKindLabel(fileType: string | null | undefined, url: string): string {
  if (fileType) {
    const sub = fileType.split("/")[1]?.toUpperCase();
    if (sub === "JPEG") return "JPG";
    if (sub === "QUICKTIME") return "MOV";
    return sub || fileType.split("/")[0]?.toUpperCase() || "FILE";
  }
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
  if (ext && ext.length <= 5 && /^[a-z0-9]+$/.test(ext)) return ext.toUpperCase();
  return "FILE";
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Resolve the best MIME hint for MediaPreview's lightbox viewer (image / pdf / video / other)
function getDocMimeType(doc: Document): string | undefined {
  if (doc.fileType) return doc.fileType;
  const path = doc.s3Url.split("?")[0].toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/.test(path)) return "image/jpeg";
  if (/\.pdf$/.test(path)) return "application/pdf";
  if (/\.(mp4|webm|mov)$/.test(path)) return "video/mp4";
  return undefined;
}

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) =>
  value ? (
    <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="text-xs text-gray-500 dark:text-gray-400 w-36 shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 text-right break-words">{value}</span>
    </div>
  ) : null;

const InfoCard = ({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden ${className ?? ""}`}>
    <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</h3>
    </div>
    <div className="bg-white dark:bg-gray-800">{children}</div>
  </div>
);

const SectionEmpty = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6M5 8h14M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
    <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

// Document card thumbnail.
// Drive URLs: always use Drive's thumbnail API (renders a JPEG preview for any
// file type — images, PDFs, docs — so thumbnails show even when fileType is null).
// S3 image URLs: loaded directly.
// PDF / generic: icon fallback.
function DocCardThumbnail({ doc, onClick }: { doc: Document; onClick: () => void }) {
  const [imgFailed, setImgFailed] = useState(false);

  const mime = (doc.fileType ?? "").toLowerCase();
  const path = doc.s3Url.split("?")[0].toLowerCase();
  const isDrive = isGoogleDriveUrl(doc.s3Url);
  const isS3Image = !isDrive && (mime.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/.test(path));
  const isPdf = mime === "application/pdf" || /\.pdf$/.test(path);

  // Drive thumbnail API always returns a renderable JPEG for any file type
  const imgSrc = isDrive ? getGoogleDriveThumbnailUrl(doc.s3Url, 400) : doc.s3Url;
  const shouldShowImage = (isDrive || isS3Image) && !imgFailed;

  if (shouldShowImage) {
    return (
      <button type="button" onClick={onClick}
        className="relative w-full h-full overflow-hidden group cursor-zoom-in focus:outline-none">
        <img
          src={imgSrc}
          alt={doc.documentName}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={() => setImgFailed(true)}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
          </svg>
        </div>
      </button>
    );
  }

  if (isPdf || imgFailed) {
    return (
      <button type="button" onClick={onClick}
        className="w-full h-full bg-red-50 dark:bg-red-950/30 flex flex-col items-center justify-center gap-1.5 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors cursor-pointer focus:outline-none">
        <svg className="w-9 h-9 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="text-[10px] font-semibold text-red-400 uppercase tracking-wide">PDF</span>
      </button>
    );
  }

  return (
    <button type="button" onClick={onClick}
      className="w-full h-full bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center gap-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer focus:outline-none">
      <svg className="w-9 h-9 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {getFileKindLabel(doc.fileType, doc.s3Url)}
      </span>
    </button>
  );
}

// Per-card download button — calls triggerDownload (fetch-as-blob) so the file
// saves to disk instead of opening in a new tab.
function DocDownloadButton({ item }: { item: MediaItem }) {
  const [busy, setBusy] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await triggerDownload(item);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={`Download ${resolveDownloadName(item)}`}
      className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {busy ? (
        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      )}
    </button>
  );
}

const EmployeeDetails = ({
  data: dataProp,
  allEmployees,
  currentIndex,
  onNavigate,
  onBack,
  onEmployeeUpdated,
  sections: customSections,
  initialSection,
}: EmployeeDetailProps) => {
  const managerJwt =
    typeof window !== "undefined" ? localStorage.getItem("accessToken") || "" : "";

  // Local copy of the selected employee so an in-screen edit reflects instantly
  // (the prop is a snapshot from the list and never changes after an update).
  // Re-synced whenever the parent hands over a different employee (prev/next nav).
  const [data, setData] = useState(dataProp);
  useEffect(() => {
    setData(dataProp);
  }, [dataProp]);

  // Hook for the document lightbox — dynamic items set on open
  const { previewProps, open: openDocPreview } = useMediaPreview();
  const { addToast } = useToaster();

  // Only admins may approve/reject documents.
  const canVerifyDocs = isAdminRole(parseJwtPayload(managerJwt));

  // Local copy of the documents so approve/reject updates reflect instantly
  // without a full employee refetch. Re-synced whenever the employee changes.
  const [docList, setDocList] = useState<Document[]>(data.documents || []);
  useEffect(() => {
    setDocList(data.documents || []);
  }, [data.documents, data.id, data.userId]);

  // Verification modal state: which document + action is being confirmed, the
  // rejection reason text, and the in-flight flag.
  const [verifyTarget, setVerifyTarget] = useState<{
    doc: Document;
    action: Extract<DocStatus, "APPROVED" | "REJECTED">;
  } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const openApprove = (doc: Document) => {
    setRejectReason("");
    setVerifyTarget({ doc, action: "APPROVED" });
  };
  const openReject = (doc: Document) => {
    setRejectReason("");
    setVerifyTarget({ doc, action: "REJECTED" });
  };
  const closeVerify = () => {
    if (isVerifying) return;
    setVerifyTarget(null);
    setRejectReason("");
  };

  // PATCH api/upload-documents/{id}/verify — rejectionReason only for REJECTED.
  const handleVerifySubmit = async () => {
    if (!verifyTarget || isVerifying) return;
    const { doc, action } = verifyTarget;
    const reason = rejectReason.trim();
    if (action === "REJECTED" && !reason) return; // submit guarded until a reason exists

    setIsVerifying(true);
    try {
      const payload =
        action === "REJECTED"
          ? { status: "REJECTED", rejectionReason: reason }
          : { status: "APPROVED" };

      await apiService.patch(`api/upload-documents/${doc.id}/verify`, payload);

      // Reflect the new state locally.
      setDocList((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? {
              ...d,
              verificationStatus: action,
              isVerified: action === "APPROVED",
              rejectionReason: action === "REJECTED" ? reason : null,
            }
            : d,
        ),
      );
      addToast(
        action === "APPROVED"
          ? `${doc.documentName} approved successfully`
          : `${doc.documentName} rejected successfully`,
        "success",
      );
      setVerifyTarget(null);
      setRejectReason("");
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ||
        (err as { message?: string })?.message ||
        "Failed to update document. Please try again.";
      addToast(message, "error");
    } finally {
      setIsVerifying(false);
    }
  };

  const defaultSections: TabSection[] = [
    {
      key: "Personal Details",
      label: "Personal",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      key: "Projects",
      label: "Projects",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      key: "Skills",
      label: "Skills",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
    },
    {
      key: "Salary",
      label: "Salary",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: "Documents",
      label: "Documents",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      key: "Assets",
      label: "Assets",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      ),
    },
    {
      key: "Bank Details",
      label: "Bank",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      key: "Work Summary",
      label: "Work",
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ];

  const sections = customSections || defaultSections;
  const [activeSection, setActiveSection] = useState<string>(() =>
    initialSection && sections.some((section) => section.key === initialSection)
      ? initialSection
      : sections[0].key,
  );
  // "Update Details" → opens the shared EditEmployee form for this employee.
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [salaryIsUpdate, setSalaryIsUpdate] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [familyDetails, setFamilyDetails] = useState<FamilyDetailsData | null>(null);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [familyError, setFamilyError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssets = async () => {
      if (activeSection === "Assets") {
        setAssetsLoading(true);
        setAssetsError(null);
        try {
          const response = await apiService.get(`api/assets/user/${data.email}`);
          setAssets(response.data || []);
        } catch (error) {
          console.error("Error fetching assets:", error);
          setAssetsError("Failed to load assets. Please try again.");
          setAssets([]);
        } finally {
          setAssetsLoading(false);
        }
      }
    };
    fetchAssets();
  }, [activeSection, data.userId, data.id]);

  useEffect(() => {
    const fetchFamilyDetails = async () => {
      const employeeId = data.id || data.userId;
      if (activeSection === "Personal Details" && employeeId) {
        setFamilyLoading(true);
        setFamilyError(null);
        setFamilyDetails(null);
        try {
          const response = await apiService.get(`api/familyDetails/${employeeId}`);
          if (!response.data || Object.keys(response.data).length === 0) {
            setFamilyDetails({});
          } else {
            setFamilyDetails(response.data);
          }
        } catch (error: unknown) {
          interface ApiError {
            response?: { status?: number };
            status?: number;
            message?: string;
          }
          const apiError = error as ApiError;
          const is404 =
            apiError?.response?.status === 404 ||
            apiError?.status === 404 ||
            apiError?.message?.includes("404") ||
            apiError?.message?.toLowerCase().includes("not found");
          if (is404) {
            setFamilyDetails({});
            setFamilyError(null);
          } else {
            setFamilyError("Failed to load family details. Please try again.");
          }
        } finally {
          setFamilyLoading(false);
        }
      }
    };
    fetchFamilyDetails();
  }, [activeSection, data.id, data.userId]);

  const personalKeys = [
    // CHANGE: "designation" added. The API returns it on every api/users row
    // (e.g. "HR manager", "Web Developer", "QA Engineer"), but the only place
    // this screen rendered it was the header <span> beside the name, which
    // carries `hidden sm:inline` and is therefore display:none at every
    // viewport width — so the employee's role was not visible anywhere on the
    // screen. Listing it here puts it in the Personal tab's details table.
    // NOTE: placed first because the role is the primary descriptor of an
    // employee; the remaining keys keep their previous order.
    "designation",
    "email",
    "mobileNumber",
    "emergencyMobileNumber",
    "gender",
    "dob",
    "doj",
    "ofcLocation",
  ];

  const personalData = [
    "tempAddress",
    "permanentAddress",
    "city",
    "state",
    "pinCode",
  ];

  const familyKeys = [
    "fatherName",
    "motherName",
    "maritalStatus",
    "spouseName",
    "bloodGroup",
    "emergencyContactName",
    "emergencyContactNumber",
  ];

  const handlePrevious = () => {
    if (currentIndex > 0) onNavigate(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < allEmployees.length - 1) onNavigate(currentIndex + 1);
  };

  const onClose = () => {
    setSalaryIsUpdate(false);
  };

  const renderSalarySection = () => {
    if (!data.salary) {
      return <SectionEmpty message="No salary data available" />;
    }

    const { salaryStructure, ...otherSalaryData } = data.salary;

    return (
      <div className="space-y-4">
        {/* Section header with Update button */}
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Salary Overview</h4>
          <button
            onClick={() => setSalaryIsUpdate(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-md text-xs font-medium transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Update Salary
          </button>
        </div>

        {/* Overview cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(otherSalaryData).map(([key, value]) => {
            if (typeof value === "object" || value === null) return null;
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{capitalizeWords(key)}</p>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{value as string}</p>
              </div>
            );
          })}
        </div>

        {/* Salary structure table */}
        {salaryStructure && (
          <InfoCard title="Salary Structure">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Component</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {Object.entries(salaryStructure).map(([key, value]) => (
                    <tr key={key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 capitalize">{capitalizeWords(key)}</td>
                      <td className="px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-gray-200 text-right">{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </InfoCard>
        )}
      </div>
    );
  };

  const renderDocumentsSection = () => {
    if (!docList || docList.length === 0) {
      return <SectionEmpty message="No documents available" />;
    }

    // Build MediaItem list for the lightbox — uses getDocMimeType so the correct
    // viewer (image / PDF iframe / video / download) is selected per file.
    const docItems: MediaItem[] = docList.map((doc: Document) => ({
      url: doc.s3Url,
      name: doc.documentName,
      mimeType: getDocMimeType(doc),
    }));

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {docList.map((document: Document, index: number) => {
          const status = getDocStatus(document);
          const isApproved = status === "APPROVED";
          const isRejected = status === "REJECTED";
          // Per requirement: actions available while PENDING, disabled once APPROVED.
          const actionsDisabled = isApproved || isVerifying;

          const badge = {
            APPROVED: { cls: "bg-green-500 text-white", label: "Approved" },
            REJECTED: { cls: "bg-red-500 text-white", label: "Rejected" },
            PENDING: { cls: "bg-amber-500 text-white", label: "Pending" },
          }[status];

          return (
            <div
              key={document.id || index}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow flex flex-col"
            >
              {/* Thumbnail — DocCardThumbnail uses Drive thumbnail API so all file
                  types (images, PDFs) render a meaningful preview image */}
              <div className="relative bg-gray-100 dark:bg-gray-900 h-36 overflow-hidden">
                <DocCardThumbnail
                  doc={document}
                  onClick={() => openDocPreview(docItems, index)}
                />

                {/* Verification status badge (Approved / Rejected / Pending) */}
                <span
                  className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold pointer-events-none z-10 ${badge.cls}`}
                >
                  {isApproved && (
                    <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                  {badge.label}
                </span>
              </div>

              {/* Card footer */}
              <div className="p-3 flex flex-col flex-1">
                {/* Document name */}
                <p
                  className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate leading-tight mb-1.5"
                  title={document.documentName}
                >
                  {document.documentName}
                </p>

                {/* Metadata: file type chip + size + upload date */}
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    {getFileKindLabel(document.fileType, document.s3Url)}
                  </span>
                  {document.size && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      {formatFileSize(document.size)}
                    </span>
                  )}
                  {document.uploadedAt && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">
                      {formatDateToMonthDayYear(document.uploadedAt)}
                    </span>
                  )}
                </div>

                {/* Rejection reason, shown when the document was rejected */}
                {isRejected && document.rejectionReason && (
                  <p
                    className="text-[10px] text-red-500 dark:text-red-400 mb-2 line-clamp-2"
                    title={document.rejectionReason}
                  >
                    Reason: {document.rejectionReason}
                  </p>
                )}

                {/* Actions. The Preview button was removed — the thumbnail above
                    opens the lightbox. Admins get Approve / Reject here instead;
                    Download is always available. Approve/Reject disable once the
                    document is approved (per requirement). */}
                <div className="flex gap-2 mt-auto">
                  {canVerifyDocs && (
                    <>
                      <button
                        type="button"
                        onClick={() => openApprove(document)}
                        disabled={actionsDisabled}
                        title={isApproved ? "Already approved" : "Approve document"}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => openReject(document)}
                        disabled={actionsDisabled}
                        title={isApproved ? "Approved documents can't be rejected" : "Reject document"}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Reject
                      </button>
                    </>
                  )}
                  <DocDownloadButton item={docItems[index]} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAssetsSection = () => {
    if (assetsLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading assets...</p>
        </div>
      );
    }

    if (assetsError) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg className="w-10 h-10 text-red-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{assetsError}</p>
          <button
            onClick={() => setActiveSection("Assets")}
            style={{ backgroundColor: colors.primary }}
            className="px-4 py-1.5 text-white text-sm rounded-md hover:opacity-90"
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <AssetsList
        assets={assets}
        gridCols={{ default: 1, md: 2, lg: 3, xl: 4 }}
        onAssetView={(id) => console.log("View asset:", id)}
        emptyStateConfig={{
          message: "No assets assigned to this employee",
          showAction: false,
        }}
      />
    );
  };

  const renderSectionData = (sectionKey: string) => {
    switch (sectionKey) {
      case "Personal Details":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoCard title="Contact Details">
                {personalKeys.map((key) => {
                  const value = data[key];
                  if (!value) return null;

                  const isDate = key === "dob" || key === "doj";
                  // NOTE: designation is entered free-hand, so the API returns
                  // it in mixed casing ("HR manager", "Software engineer",
                  // "Web Developer"). It gets the same `capitalize` treatment
                  // the Address Information rows already use, so the column
                  // reads consistently instead of mirroring the data entry.
                  // Written as if/else rather than a nested ternary (Sonar).
                  let renderedValue: React.ReactNode = value;
                  if (isDate) {
                    renderedValue = formatDateToMonthDayYear(value);
                  } else if (key === "designation") {
                    renderedValue = <span className="capitalize">{value}</span>;
                  }

                  return (
                    <InfoRow
                      key={key}
                      label={isDate ? key.toUpperCase() : capitalizeWords(key)}
                      value={renderedValue}
                    />
                  );
                })}
              </InfoCard>

              <InfoCard title="Address Information">
                {personalData.map((key) => {
                  const value = data[key];
                  if (!value) return null;
                  return <InfoRow key={key} label={capitalizeWords(key)} value={<span className="capitalize">{value}</span>} />;
                })}
              </InfoCard>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(data.profileDetails?.aadhaar || data.PAN) && (
                <InfoCard title="Identity Details">
                  {data.profileDetails?.aadhaar && (
                    <InfoRow label="Aadhaar Number" value={<span className="font-mono tracking-widest">{data.profileDetails.aadhaar}</span>} />
                  )}
                  {data.PAN && (
                    <InfoRow label="PAN Number" value={<span className="font-mono uppercase tracking-widest">{data.PAN}</span>} />
                  )}
                  {data.UAN && (
                    <InfoRow label="UAN Number" value={<span className="font-mono uppercase tracking-widest">{data.UAN}</span>} />
                  )}
                </InfoCard>
              )}

              <InfoCard title="Family Details">
                <div className="relative min-h-[60px]">
                  {familyLoading && (
                    <div className="absolute inset-0 z-10 bg-white/80 dark:bg-gray-800/80 flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs text-gray-500">Loading...</span>
                    </div>
                  )}

                  {familyError && !familyLoading && (
                    <div className="px-4 py-4 text-center">
                      <p className="text-sm text-red-500 mb-2">{familyError}</p>
                      <button
                        onClick={() => setFamilyDetails(null)}
                        className="text-xs px-3 py-1 bg-red-50 text-red-600 rounded-md hover:bg-red-100"
                      >
                        Retry
                      </button>
                    </div>
                  )}

                  {!familyLoading && !familyError && familyDetails && (
                    <>
                      {familyKeys.map((key) => {
                        const value = familyDetails[key];
                        if (!value) return null;
                        return (
                          <InfoRow
                            key={key}
                            label={capitalizeWords(key)}
                            value={<span className="capitalize">{value as string | number}</span>}
                          />
                        );
                      })}

                      {familyDetails.children && familyDetails.children.length > 0 && (
                        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                            Children ({familyDetails.numberOfChildren || familyDetails.children.length})
                          </p>
                          <div className="space-y-2">
                            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                            {familyDetails.children.map((child: any) => (
                              <div key={child.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-md border border-gray-200 dark:border-gray-600 p-2.5">
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  <div>
                                    <p className="text-gray-400 mb-0.5">Name</p>
                                    <p className="font-medium text-gray-800 dark:text-gray-200">{child.childName}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400 mb-0.5">DOB</p>
                                    <p className="font-medium text-gray-800 dark:text-gray-200">{formatDateToMonthDayYear(child.childDob)}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400 mb-0.5">Gender</p>
                                    <p className="font-medium text-gray-800 dark:text-gray-200 capitalize">{child.gender}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!familyKeys.some((key: string) => familyDetails[key]) &&
                        (!familyDetails.children || familyDetails.children.length === 0) && (
                          <div className="px-4 py-5 text-center text-sm text-gray-500 dark:text-gray-400">
                            No family details added yet.
                          </div>
                        )}
                    </>
                  )}
                </div>
              </InfoCard>
            </div>
          </div>
        );

      case "Skills":
        return (
          <div>
            {data.skills && data.skills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.skills.map((skill: Skill) => (
                  <span
                    key={skill.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors"
                  >
                    <span className="w-6 h-6 rounded-md bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {skill.skill.charAt(0).toUpperCase()}
                    </span>
                    <span className="capitalize">{skill.skill}</span>
                  </span>
                ))}
              </div>
            ) : (
              <SectionEmpty message="No skills available" />
            )}
          </div>
        );

      case "Projects":
        return (
          <div>
            {data.userProjectWithAssignedDate && data.userProjectWithAssignedDate.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {data.userProjectWithAssignedDate.map((project: Project) => (
                  <div
                    key={project.projectId}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 capitalize">{project.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Assigned {formatDateToMonthDayYear(project.projectAssignedDate)}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      <InfoRow label="Manager" value={<span className="capitalize">{project.manager}</span>} />
                      <InfoRow label="Team Size" value={project.teamSize} />
                      <InfoRow
                        label="Timeline"
                        value={`${formatDateToMonthDayYear(project.startDate)} — ${formatDateToMonthDayYear(project.completionDate)}`}
                      />
                      <InfoRow
                        label="Client"
                        value={
                          <>
                            <span className="capitalize">{project.clientContactName}</span>
                            {project.clientContactNumber && (
                              <span className="text-gray-500 ml-1">({project.clientContactNumber})</span>
                            )}
                          </>
                        }
                      />
                      {project.projectDetail && (
                        <div className="px-4 py-2.5">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Details</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{project.projectDetail}</p>
                        </div>
                      )}
                      {project.comments && (
                        <div className="px-4 py-2.5 bg-gray-50/50 dark:bg-gray-700/30">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Comments</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 italic">{project.comments}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <SectionEmpty message="No projects available" />
            )}
          </div>
        );

      case "Salary":
        return renderSalarySection();

      case "Bank Details":
        return (
          <div>
            {data.profileDetails ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoCard title="Bank Account" className="md:col-span-2">
                  <InfoRow label="Account Holder Name" value={<span className="capitalize">{data.profileDetails.accountHolderName || "—"}</span>} />
                  <InfoRow label="Bank Name" value={<span className="capitalize">{data.profileDetails.bankName || "—"}</span>} />
                  <InfoRow label="Account Number" value={<span className="font-mono tracking-wider">{data.profileDetails.accountNumber || "—"}</span>} />
                  <InfoRow label="IFSC Code" value={<span className="font-mono uppercase tracking-widest">{data.profileDetails.ifscCode || "—"}</span>} />
                  <InfoRow label="Account Type" value={<span className="capitalize">{data.profileDetails.accountType || "—"}</span>} />
                  <InfoRow label="Branch Name" value={<span className="capitalize">{data.profileDetails.branchName || "—"}</span>} />
                </InfoCard>

                {(data.profileDetails?.aadhaar || data.PAN) && (
                  <InfoCard title="Identity Documents" className="md:col-span-2">
                    {data.profileDetails?.aadhaar && (
                      <InfoRow
                        label="Aadhaar Number"
                        value={<span className="font-mono tracking-widest">{data.profileDetails.aadhaar}</span>}
                      />
                    )}
                    {data.PAN && (
                      <InfoRow
                        label="PAN Number"
                        value={<span className="font-mono uppercase tracking-widest">{data.PAN}</span>}
                      />
                    )}
                  </InfoCard>
                )}
              </div>
            ) : (
              <SectionEmpty message="No bank details available" />
            )}
          </div>
        );

      case "Documents":
        return renderDocumentsSection();

      case "Assets":
        return renderAssetsSection();

      case "Work Summary":
        return (
          <WorkSummary
            employeeId={data.userId || data.id}
            employeeName={data.displayName}
            employeeEmail={data.email}
            managerJwt={managerJwt}
          />
        );

      default:
        return <SectionEmpty message="No data available" />;
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Profile + Tabs card */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        {/* Profile row - Padding adjusted to match the tabs for even vertical spacing */}
        <div className="flex items-center gap-3 px-4 py-2.5">
          <button
            onClick={onBack}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
            title="Back to Employee Listing"
          >
            <ArrowLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>

          <img
            src={data.imageUrl || Image}
            alt={data.displayName}
            className="w-12 h-12 rounded-full object-cover ring-2 ring-blue-100 dark:ring-gray-600 border-2 border-white dark:border-gray-700 flex-shrink-0"
            loading="lazy"
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white capitalize">
                {data.displayName}
              </h2>
              {data.designation && (
                <span className="text-sm text-gray-500 dark:text-gray-400 capitalize hidden sm:inline">
                  · {data.designation}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${data.userStatus === "ACTIVE"
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${data.userStatus === "ACTIVE" ? "bg-green-500" : "bg-gray-400"
                    }`}
                />
                {data.userStatus || "Active"}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {(data.department || data.departmentName) && (
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  {data.department || data.departmentName}
                </span>
              )}
              {data.doj && (
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  Joined {formatDateToMonthDayYear(data.doj)}
                </span>
              )}
              {data.ofcLocation && (
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  {data.ofcLocation}
                </span>
              )}
              {data.employmentType && (
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  {data.employmentType}
                </span>
              )}
            </div>
          </div>

          {/* Update Details — opens the shared EditEmployee form (admins only). */}
          {canVerifyDocs && (
            <HeaderButtons
              type="button"
              variant="primary"
              size="small"
              onClick={() => setIsEditOpen(true)}
              className="flex-shrink-0 whitespace-nowrap"
              icon={
                <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              }
            >
              Update Details
            </HeaderButtons>
          )}

          {/* Navigation */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400 hidden md:block tabular-nums">
              {currentIndex + 1} / {allEmployees.length}
            </span>
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Previous Employee"
            >
              <ArrowLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={handleNext}
              disabled={currentIndex === allEmployees.length - 1}
              className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Next Employee"
            >
              <ArrowRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Tab bar - Removed border-t to fix the floating line issue */}
        <div>
          <nav className="flex overflow-x-auto">
            {sections.map((section: TabSection) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${activeSection === section.key
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  }`}
              >
                {section.icon}
                <span>{section.label}</span>
                {section.key === "Documents" && data.documents && data.documents.length > 0 && (
                  <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none">
                    {data.documents.length}
                  </span>
                )}
                {section.key === "Projects" && data.userProjectWithAssignedDate && data.userProjectWithAssignedDate.length > 0 && (
                  <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none">
                    {data.userProjectWithAssignedDate.length}
                  </span>
                )}
                {section.key === "Skills" && data.skills && data.skills.length > 0 && (
                  <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none">
                    {data.skills.length}
                  </span>
                )}
                {activeSection === section.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Scrollable content area.
          CHANGE: added px-4 so the tab content keeps the same 16px side gutter
          the rest of the app uses (DATA_TABLE_GUTTER_CLASS = "pl-4", and the
          profile/tab bar above is px-4). Without it this column had vertical
          padding only, so every section card sat flush against the sidebar
          border on the left and the window edge on the right, and the cards
          did not line up with the back button or the tab labels above them. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {renderSectionData(activeSection)}
      </div>

      {/* Update Details — the shared EditEmployee form, pre-filled with this
          employee's details. Keeps the exact same edit process used in the list. */}
      {isEditOpen && (
        <GenericModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          header="Edit Employee"
          customWidth="min(880px, 95vw)"
        >
          <EditEmployee
            data={data}
            onClose={() => setIsEditOpen(false)}
            onSuccess={(msg, updatedEmployee) => {
              addToast(msg, "success");
              // Merge the updated fields onto the current record so the details
              // screen immediately shows the new values (keeps documents, salary,
              // projects etc. from the existing copy).
              if (updatedEmployee) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setData((prev: any) => ({ ...prev, ...updatedEmployee }));
              }
              // Tell the parent list to refetch so the table row isn't stale
              // when the user navigates back.
              onEmployeeUpdated?.();
              setIsEditOpen(false);
            }}
            onError={(msg) => addToast(msg, "error")}
          />
        </GenericModal>
      )}

      {salaryIsUpdate && (
        <GenericModal
          isOpen={salaryIsUpdate}
          onClose={onClose}
          header="Update Salary Information"
          customWidth="max-w-4xl"
        >
          <div className="p-1">
            <div className="mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Update employee&apos;s salary details and structure
              </span>
            </div>
            {data.salary && (
              <UpdateSalaryDetails
                refresh={refresh}
                setRefresh={setRefresh}
                onClose={onClose}
                updateValues={data.salary}
              />
            )}
          </div>
        </GenericModal>
      )}

      {/* Approve / Reject confirmation modal */}
      {verifyTarget && (
        <GenericModal
          isOpen={!!verifyTarget}
          onClose={closeVerify}
          header={verifyTarget.action === "APPROVED" ? "Approve Document" : "Reject Document"}
          size="md"
        >
          <div className="p-1">
            <div className="flex items-start gap-3 mb-4">
              <div
                className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${verifyTarget.action === "APPROVED"
                  ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                  : "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                  }`}
              >
                {verifyTarget.action === "APPROVED" ? (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm text-gray-700 dark:text-gray-200">
                  {verifyTarget.action === "APPROVED" ? (
                    <>Are you sure you want to approve <span className="font-semibold">{verifyTarget.doc.documentName}</span>?</>
                  ) : (
                    <>Please provide a reason for rejecting <span className="font-semibold">{verifyTarget.doc.documentName}</span>.</>
                  )}
                </p>
              </div>
            </div>

            {/* Rejection reason — required before the submit button enables */}
            {verifyTarget.action === "REJECTED" && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  autoFocus
                  placeholder="e.g. The document is blurry, please upload a clearer copy."
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeVerify}
                disabled={isVerifying}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleVerifySubmit}
                disabled={
                  isVerifying ||
                  (verifyTarget.action === "REJECTED" && !rejectReason.trim())
                }
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${verifyTarget.action === "APPROVED"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
                  }`}
              >
                {isVerifying && (
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                )}
                {verifyTarget.action === "APPROVED"
                  ? isVerifying
                    ? "Approving…"
                    : "Approve"
                  : isVerifying
                    ? "Rejecting…"
                    : "Submit"}
              </button>
            </div>
          </div>
        </GenericModal>
      )}

      {/* Document lightbox — renders as a portal-style overlay when open */}
      <MediaPreview {...previewProps} />
    </div>
  );
};

export default EmployeeDetails;
